// src/services/FileService.ts

import * as pdfjsLib from 'pdfjs-dist';

// Modern PDF.js v5 worker configuration
try {
    if (pdfjsLib) {
        // @ts-ignore - pdfjsLib version and GlobalWorkerOptions might not be fully typed in all environments
        pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;
    }
} catch (e) {
    console.error('Failed to initialize PDF.js worker:', e);
}

class FileService {
    /**
     * Extracts text from a file (PDF or text-based)
     * @param file - The file object to extract text from
     * @returns Promise resolving to the extracted text
     */
    async extractText(file: File): Promise<string> {
        const extension = file.name.split('.').pop()?.toLowerCase();

        switch (extension) {
            case 'pdf':
                return this.extractTextFromPDF(file);
            case 'docx':
                return 'Word document extraction is planned for the next update. Please copy-paste text for now.';
            case 'xlsx':
            case 'xls':
                return 'Excel comparison is planned for the next update. Please copy-paste text for now.';
            default:
                return this.readTextFile(file);
        }
    }

    /**
     * Reads a plain text file
     * @param file - The file object to read
     * @returns Promise resolving to the file content
     */
    readTextFile(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target?.result as string);
            reader.onerror = () => reject(new Error('Failed to read text file'));
            reader.readAsText(file);
        });
    }

    /**
     * Extracts text from PDF using PDF.js locally
     * @param file - The PDF file to extract text from
     * @returns Promise resolving to the extracted PDF text
     */
    async extractTextFromPDF(file: File): Promise<string> {
        try {
            const arrayBuffer = await file.arrayBuffer();
            const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
            const pdf = await loadingTask.promise;
            let fullText = '';

            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const textContent = await page.getTextContent();
                // @ts-ignore - item has 'str' property in TextContent
                const pageText = textContent.items.map((item: any) => item.str).join(' ');
                fullText += pageText + '\n\n';
            }

            return fullText.trim();
        } catch (error) {
            console.error('PDF Extraction error:', error);
            throw new Error(
                'Failed to extract text from PDF locally. The file might be protected or malformed.'
            );
        }
    }
}

const fileServiceInstance = new FileService();
export default fileServiceInstance;
